import React, { useState, useEffect, useRef } from 'react';

// Fallback list (U1..U25) used only when no real options are supplied.
const DEFAULT_AGE_GROUPS = Array.from({ length: 25 }, (_, i) => "U" + (i + 1));

export type AgeGroupSelectProps = {
  value: string | string[];
  onChange: (value: string[]) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  className?: string;
  // Real age-group names (from admin/age-groups). When provided, these
  // replace the hardcoded U1..U25 list so the picker matches the platform's
  // actual age groups.
  options?: string[];
};

export default function AgeGroupSelect({
  value,
  onChange,
  label,
  placeholder = 'Select age groups...',
  disabled = false,
  multiple = true,
  className = '',
  options,
}: AgeGroupSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const AGE_GROUPS = options && options.length > 0 ? options : DEFAULT_AGE_GROUPS;

  // Filter age groups based on search term
  const filteredAgeGroups = AGE_GROUPS.filter(ageGroup =>
    ageGroup.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const toggleOption = (ageGroup: string) => {
    if (multiple) {
      if (selectedValues.includes(ageGroup)) {
        onChange(selectedValues.filter(v => v !== ageGroup));
      } else {
        onChange([...selectedValues, ageGroup]);
      }
    } else {
      onChange([ageGroup]);
      setIsOpen(false);
    }
  };

  const selectedLabels = selectedValues.filter(v => AGE_GROUPS.includes(v));
  const btnClass = 'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ' + (disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer');
  const svgClass = 'absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 transition-transform ' + (isOpen ? 'rotate-180' : '');

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      )}
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={btnClass}
        >
          {selectedLabels.length > 0 ? (
            <span className="text-slate-700">{selectedLabels.join(', ')}</span>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
          <svg className={svgClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
            {/* Search input */}
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search age groups..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-lg placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="max-h-48 overflow-auto">
              {/* Clear option for single-select mode */}
              {!multiple && selectedValues.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    onChange([]);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className="w-full flex items-center px-4 py-2 hover:bg-red-50 cursor-pointer text-red-600 border-b border-slate-100"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-sm">Clear selection</span>
                </button>
              )}
              {filteredAgeGroups.length === 0 ? (
                <div className="px-4 py-3 text-slate-500 text-sm text-center">No age groups match your search</div>
              ) : (
                filteredAgeGroups.map(ageGroup => (
                  <label key={ageGroup} className="flex items-center px-4 py-2 hover:bg-slate-50 cursor-pointer">
                    <input
                      type={multiple ? 'checkbox' : 'radio'}
                      checked={selectedValues.includes(ageGroup)}
                      onChange={() => toggleOption(ageGroup)}
                      className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-3 text-sm text-slate-700">{ageGroup}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
